import { getApiUrl } from "../config";
import { responseErrorMessage } from "../error";
import { request } from "../request";

export interface LoginResponse {
  token: string;
  username: string;
  user_id?: string | null;
  role?: UserRole | null;
  message?: string;
  permissions?: string[];
}

export interface AuthStatusResponse {
  enabled: boolean;
  has_users: boolean;
  mode?: "hub";
  bootstrap_required?: boolean;
  registration_enabled?: boolean;
}

export type UserRole = "admin" | "manager" | "user" | "readonly";
export type BusinessRole = "owner" | "member";

export interface BusinessDirection {
  id: string;
  name: string;
  description: string;
  agent_ids: string[];
  knowledge_base_ids?: string[];
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AuthMeResponse {
  user_id: string;
  username: string;
  role: UserRole;
  permissions: string[];
  denied_permissions: string[];
  business_roles: Record<string, BusinessRole>;
}

export interface AuthUser {
  user_id: string;
  username: string;
  role: UserRole;
  roles: string[];
  disabled: boolean;
  token_version: number;
  permissions: string[];
  denied_permissions: string[];
  business_roles: Record<string, BusinessRole>;
  metadata: Record<string, unknown>;
  created_at?: string | null;
  updated_at?: string | null;
  last_login_at?: string | null;
}

export interface CreateUserPayload {
  username: string;
  password: string;
  role: UserRole;
  permissions?: string[];
  denied_permissions?: string[];
  business_roles?: Record<string, BusinessRole>;
  disabled?: boolean;
}

export interface UpdateUserPayload {
  username?: string;
  role?: UserRole;
  permissions?: string[];
  denied_permissions?: string[];
  business_roles?: Record<string, BusinessRole>;
  disabled?: boolean;
}

export interface BusinessDirectionPayload {
  id?: string;
  name: string;
  description?: string;
  agent_ids?: string[];
  knowledge_base_ids?: string[];
}

export const authApi = {
  login: async (username: string, password: string): Promise<LoginResponse> => {
    const res = await fetch(getApiUrl("/auth/login"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      throw new Error(await responseErrorMessage(res, "Login failed"));
    }
    return res.json();
  },

  register: async (
    username: string,
    password: string,
  ): Promise<LoginResponse> => {
    const res = await fetch(getApiUrl("/auth/register"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      throw new Error(await responseErrorMessage(res, "Registration failed"));
    }
    return res.json();
  },

  getStatus: async (): Promise<AuthStatusResponse> => {
    const res = await fetch(getApiUrl("/auth/status"));
    if (!res.ok) throw new Error("Failed to check auth status");
    return res.json();
  },

  updateProfile: async (
    currentPassword: string,
    newUsername?: string,
    newPassword?: string,
  ): Promise<LoginResponse> => {
    const token = localStorage.getItem("qwenpaw_auth_token") || "";
    const res = await fetch(getApiUrl("/auth/update-profile"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_username: newUsername || null,
        new_password: newPassword || null,
      }),
    });
    if (!res.ok) {
      throw new Error(await responseErrorMessage(res, "Update failed"));
    }
    return res.json();
  },

  me: () => request<AuthMeResponse>("/auth/me"),

  listUsers: () => request<{ users: AuthUser[] }>("/auth/users"),

  listBusinessDirections: () =>
    request<{ business_directions: BusinessDirection[] }>(
      "/auth/business-directions",
    ),

  createBusinessDirection: (payload: BusinessDirectionPayload) =>
    request<BusinessDirection>("/auth/business-directions", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateBusinessDirection: (
    directionId: string,
    payload: Partial<BusinessDirectionPayload>,
  ) =>
    request<BusinessDirection>(
      `/auth/business-directions/${encodeURIComponent(directionId)}`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    ),

  listKbTargets: () =>
    request<{ items: Array<{ id: string; name: string; alias?: string }> }>(
      "/kb-binding/kb-targets",
    ),

  deleteBusinessDirection: (directionId: string) =>
    request<{ deleted: boolean; id: string }>(
      `/auth/business-directions/${encodeURIComponent(directionId)}`,
      {
        method: "DELETE",
      },
    ),

  createUser: (payload: CreateUserPayload) =>
    request<AuthUser>("/auth/users", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateUser: (userId: string, payload: UpdateUserPayload) =>
    request<AuthUser>(`/auth/users/${encodeURIComponent(userId)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  setUserPassword: (userId: string, password: string) =>
    request<AuthUser>(`/auth/users/${encodeURIComponent(userId)}/password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  revokeUserTokens: (userId: string) =>
    request<{ revoked: boolean; user_id: string }>(
      `/auth/users/${encodeURIComponent(userId)}/revoke-tokens`,
      {
        method: "POST",
      },
    ),
};
